// API Client utility for making authenticated requests
import config from '../config/config'
import { TokenData } from '../types/auth'
import { ApiError, ApiErrorResponse } from '../types/errors'
import { UserManagementDto } from '../dtos/userManagement.dto'
import { TrackingLogDto } from '../dtos/trackingLog.dto'
import { PCSegmentDataDto } from '../dtos/pcSegment.dto'
import { RichMenuDataDto } from '../dtos/richMenu.dto'

// Re-export ApiError for convenience
export { ApiError } from '../types/errors'

// Paginated response structure
export interface PaginatedResponse<T> {
  data: T[]
  meta: {
    page: number
    pageSize: number
    totalPages: number
    totalItems: number
    hasNext: boolean
    hasPrevious: boolean
  }
}

// Construct API base URL - remove trailing /api if present to avoid double /api
// Ensure we always have a valid base URL
let API_BASE_URL = config.apiUrl?.replace(/\/api\/?$/, '') || ''
if (!API_BASE_URL || API_BASE_URL.trim() === '') {
  console.error('[API Client] API_BASE_URL is not configured. Check REACT_APP_API_URL environment variable.')
  console.error('[API Client] Current config.apiUrl:', config.apiUrl)
  API_BASE_URL = 'https://cpfgs-lineoa-smartpc-api-dev.eggdigital.com'
}
// Ensure no trailing slash
API_BASE_URL = API_BASE_URL.replace(/\/$/, '')

// Log the API base URL in development mode for debugging
if (config.enableDebug) {
  console.log('[API Client] API_BASE_URL configured as:', API_BASE_URL)
}

// Token storage keys
const ACCESS_TOKEN_KEY = 'access_token'
const REFRESH_TOKEN_KEY = 'refresh_token'
const TOKEN_EXPIRES_AT_KEY = 'token_expires_at'
const TOKEN_TYPE_KEY = 'token_type'

// Get stored token data
export const getTokenData = (): TokenData | null => {
  const accessToken = localStorage.getItem(ACCESS_TOKEN_KEY)
  const refreshToken = localStorage.getItem(REFRESH_TOKEN_KEY)
  const expiresAt = localStorage.getItem(TOKEN_EXPIRES_AT_KEY)
  const tokenType = localStorage.getItem(TOKEN_TYPE_KEY) || 'Bearer'

  if (!accessToken || !refreshToken || !expiresAt) {
    return null
  }

  const expiresIn = Math.max(0, Math.floor((parseInt(expiresAt) - Date.now()) / 1000))

  return {
    access_token: accessToken,
    refresh_token: refreshToken,
    expires_in: expiresIn,
    expires_at: parseInt(expiresAt),
    token_type: tokenType
  }
}

// Store token data
export const setTokenData = (tokenData: { access_token: string; refresh_token: string; expires_in: number; token_type?: string }): void => {
  const expiresAt = Date.now() + (tokenData.expires_in * 1000)
  
  localStorage.setItem(ACCESS_TOKEN_KEY, tokenData.access_token)
  localStorage.setItem(REFRESH_TOKEN_KEY, tokenData.refresh_token)
  localStorage.setItem(TOKEN_EXPIRES_AT_KEY, expiresAt.toString())
  localStorage.setItem(TOKEN_TYPE_KEY, tokenData.token_type || 'Bearer')
}

// Clear token data
export const clearTokenData = (): void => {
  localStorage.removeItem(ACCESS_TOKEN_KEY)
  localStorage.removeItem(REFRESH_TOKEN_KEY)
  localStorage.removeItem(TOKEN_EXPIRES_AT_KEY)
  localStorage.removeItem(TOKEN_TYPE_KEY)
}

// Check if token is expired or will expire soon (within 5 minutes)
export const isTokenExpired = (): boolean => {
  const tokenData = getTokenData()
  if (!tokenData) return true
  
  // Check if token expires within 5 minutes
  const bufferTime = 5 * 60 * 1000 // 5 minutes in milliseconds
  return Date.now() >= (tokenData.expires_at - bufferTime)
}

// Get access token
export const getAccessToken = (): string | null => {
  const tokenData = getTokenData()
  return tokenData?.access_token || null
}

// API Request options
export interface ApiRequestOptions {
  method?: 'GET' | 'POST' | 'PUT' | 'DELETE' | 'PATCH'
  headers?: Record<string, string>
  body?: any
  params?: Record<string, string | number | undefined>
  requireAuth?: boolean
  skipTokenRefresh?: boolean
}

// Make an API request with automatic token handling
export const apiRequest = async <T = any>(
  endpoint: string,
  options: ApiRequestOptions = {}
): Promise<T> => {
  const {
    method = 'GET',
    headers = {},
    body,
    params,
    requireAuth = true,
    skipTokenRefresh = false
  } = options

  // Build URL with query parameters
  let url = `${API_BASE_URL}${endpoint.startsWith('/') ? endpoint : `/${endpoint}`}`
  
  // Add query parameters if present
  if (params) {
    const queryParams = new URLSearchParams()
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined && value !== null) {
        queryParams.append(key, String(value))
      }
    })
    const queryString = queryParams.toString()
    if (queryString) {
      url += `?${queryString}`
    }
  }

  // Prepare headers
  const requestHeaders: Record<string, string> = {
    'Content-Type': 'application/json',
    'accept': '*/*',
    ...headers
  }

  // Add authorization header if needed
  if (requireAuth && !skipTokenRefresh) {
    const tokenData = getTokenData()
    if (tokenData && !isTokenExpired()) {
      requestHeaders['Authorization'] = `${tokenData.token_type} ${tokenData.access_token}`
    }
  }

  // Create abort controller for timeout
  const controller = new AbortController()
  const timeoutId = setTimeout(() => controller.abort(), config.apiTimeout)

  // Prepare request config
  const requestConfig: RequestInit = {
    method,
    headers: requestHeaders,
    signal: controller.signal
  }

  // Add body if present
  if (body && method !== 'GET') {
    requestConfig.body = JSON.stringify(body)
  }

  try {
    const response = await fetch(url, requestConfig)
    clearTimeout(timeoutId) // Clear timeout if request completes

    // Handle unauthorized - token might be expired
    if (response.status === 401 && requireAuth && !skipTokenRefresh) {
      // Try to refresh token
      const refreshSuccess = await refreshAccessToken()
      if (refreshSuccess) {
        // Retry the original request with new token
        const retryController = new AbortController()
        const retryTimeoutId = setTimeout(() => retryController.abort(), config.apiTimeout)
        const tokenData = getTokenData()
        if (tokenData) {
          requestHeaders['Authorization'] = `${tokenData.token_type} ${tokenData.access_token}`
          requestConfig.headers = requestHeaders
          requestConfig.signal = retryController.signal
          try {
            const retryResponse = await fetch(url, requestConfig)
            clearTimeout(retryTimeoutId)
            return handleResponse<T>(retryResponse)
          } catch (retryError) {
            clearTimeout(retryTimeoutId)
            throw retryError
          }
        }
      } else {
        // Refresh failed, clear tokens
        clearTokenData()
        throw new ApiError({
          timestamp: new Date().toISOString(),
          status: 401,
          error: 'Authentication Failed',
          message: 'Authentication failed. Please login again.'
        })
      }
    }

    return handleResponse<T>(response)
  } catch (error) {
    clearTimeout(timeoutId) // Clear timeout on error
    if (error instanceof ApiError) {
      throw error
    }
    if (error instanceof Error) {
      if (error.name === 'AbortError') {
        throw new ApiError({
          timestamp: new Date().toISOString(),
          status: 408,
          error: 'Timeout',
          message: 'Request timeout. Please try again.'
        })
      }
      throw error
    }
    throw new Error('An unexpected error occurred')
  }
}

// Handle API response
const handleResponse = async <T>(response: Response): Promise<T> => {
  const contentType = response.headers.get('content-type')
  const isJson = contentType && contentType.includes('application/json')

  if (!response.ok) {
    // Try to parse structured error response from API
    let errorResponse: ApiErrorResponse = {
      timestamp: new Date().toISOString(),
      status: response.status,
      error: response.statusText || 'Error',
      message: `Request failed with status ${response.status}`,
      details: undefined
    }

    try {
      if (isJson) {
        const errorData = await response.json()
        // Merge with parsed error data from server
        errorResponse = {
          timestamp: errorData.timestamp || errorResponse.timestamp,
          status: errorData.status || response.status,
          error: errorData.error || errorResponse.error,
          message: errorData.message || errorData.error || errorResponse.message,
          details: errorData.details || errorResponse.details,
          errors: errorData.errors,        // Field-level validation errors
          errorCode: errorData.errorCode,  // RichMenu error codes
          importType: errorData.importType // Import error types
        }
      } else {
        const textError = await response.text()
        if (textError) {
          errorResponse.message = textError
        }
      }
    } catch (e) {
      // Unable to parse error, use default error response
    }

    throw new ApiError(errorResponse)
  }

  if (isJson) {
    return await response.json()
  }

  return (await response.text()) as T
}

// Refresh access token
export const refreshAccessToken = async (): Promise<boolean> => {
  try {
    const tokenData = getTokenData()
    if (!tokenData?.refresh_token) {
      return false
    }

    const response = await apiRequest<{ access_token: string; token_type?: string; expires_in: number; refresh_token?: string }>(
      '/auth/user/refresh',
      {
        method: 'POST',
        body: { refresh_token: tokenData.refresh_token },
        requireAuth: false,
        skipTokenRefresh: true
      }
    )

    // Update tokens
    const updatedTokenData = {
      access_token: response.access_token,
      refresh_token: response.refresh_token || tokenData.refresh_token, // Keep old refresh token if not provided
      expires_in: response.expires_in,
      token_type: response.token_type || tokenData.token_type
    }

    setTokenData(updatedTokenData)
    return true
  } catch (error) {
    console.error('Failed to refresh token:', error)
    return false
  }
}

// Auth API functions
export const authApi = {
  login: async (email: string, password: string) => {
    return apiRequest<{ access_token: string; token_type: string; expires_in: number; refresh_token: string }>(
      '/auth/user/login',
      {
        method: 'POST',
        body: { email, password },
        requireAuth: false,
        skipTokenRefresh: true
      }
    )
  },

  refresh: async (refreshToken: string) => {
    return apiRequest<{ access_token: string; token_type: string; expires_in: number; refresh_token?: string }>(
      '/auth/user/refresh',
      {
        method: 'POST',
        body: { refresh_token: refreshToken },
        requireAuth: false,
        skipTokenRefresh: true
      }
    )
  }
}

// User API functions
export const userApi = {
  searchUsers: async (username?: string, page: number = 0, size: number = 20) => {
    return apiRequest<PaginatedResponse<UserManagementDto>>(
      '/api/users',
      {
        method: 'GET',
        params: {
          username,
          page,
          size
        },
        requireAuth: true
      }
    )
  },

  createUser: async (username: string, email: string, password: string) => {
    return apiRequest<UserManagementDto>(
      '/api/users',
      {
        method: 'POST',
        body: {
          username,
          email,
          password
        },
        requireAuth: true
      }
    )
  },

  updateUser: async (id: number, username: string, email: string, password?: string) => {
    return apiRequest<UserManagementDto>(
      `/api/users/${id}`,
      {
        method: 'PUT',
        body: {
          username,
          email,
          ...(password && { password })
        },
        requireAuth: true
      }
    )
  },

  getUserById: async (id: number) => {
    return apiRequest<UserManagementDto>(
      `/api/users/${id}`,
      {
        method: 'GET',
        requireAuth: true
      }
    )
  },

  deleteUser: async (id: number) => {
    return apiRequest<{}>(
      `/api/users/${id}`,
      {
        method: 'DELETE',
        requireAuth: true
      }
    )
  }
}

// Tracking Log API functions
export const trackingLogApi = {
  searchTrackingLogs: async (
    employeeId?: string,
    employeeName?: string,
    startDate?: string,
    endDate?: string,
    page: number = 0,
    size: number = 20
  ) => {
    return apiRequest<PaginatedResponse<TrackingLogDto>>(
      '/api/tracking-log',
      {
        method: 'GET',
        params: {
          employeeId,
          employeeName,
          startDate,
          endDate,
          page,
          size
        },
        requireAuth: true
      }
    )
  },

  exportTrackingLogs: async (
    employeeId?: string,
    employeeName?: string,
    startDate?: string,
    endDate?: string
  ): Promise<Blob> => {
    const url = `${API_BASE_URL}/api/tracking-log/export`
    
    // Build query parameters
    const queryParams = new URLSearchParams()
    if (employeeId) queryParams.append('employeeId', employeeId)
    if (employeeName) queryParams.append('employeeName', employeeName)
    if (startDate) queryParams.append('startDate', startDate)
    if (endDate) queryParams.append('endDate', endDate)
    
    const queryString = queryParams.toString()
    const fullUrl = queryString ? `${url}?${queryString}` : url

    // Get token for authorization
    const tokenData = getTokenData()
    const headers: Record<string, string> = {
      'accept': '*/*'
    }
    
    if (tokenData && !isTokenExpired()) {
      headers['Authorization'] = `${tokenData.token_type} ${tokenData.access_token}`
    }

    // Create abort controller for timeout
    const controller = new AbortController()
    const timeoutId = setTimeout(() => controller.abort(), config.apiTimeout)

    try {
      const response = await fetch(fullUrl, {
        method: 'GET',
        headers,
        signal: controller.signal
      })

      clearTimeout(timeoutId)

      if (!response.ok) {
        throw new ApiError({
          timestamp: new Date().toISOString(),
          status: response.status,
          error: 'Export Error',
          message: `Export failed with status ${response.status}`
        })
      }

      // Return blob for CSV file
      return await response.blob()
    } catch (error) {
      clearTimeout(timeoutId)
      if (error instanceof ApiError) {
        throw error
      }
      if (error instanceof Error) {
        if (error.name === 'AbortError') {
          throw new ApiError({
            timestamp: new Date().toISOString(),
            status: 408,
            error: 'Timeout',
            message: 'Request timeout. Please try again.'
          })
        }
        throw error
      }
      throw new Error('An unexpected error occurred')
    }
  }
}

// Segments API functions
export const segmentsApi = {
  searchSegments: async (name?: string, page: number = 0, size: number = 20) => {
    return apiRequest<PaginatedResponse<PCSegmentDataDto>>(
      '/api/segments',
      {
        method: 'GET',
        params: {
          name,
          page,
          size
        },
        requireAuth: true
      }
    )
  },
  getSegmentById: async (id: string | number) => {
    return apiRequest<PCSegmentDataDto>(
      `/api/segments/${id}`,
      {
        method: 'GET',
        requireAuth: true
      }
    )
  },
  exportSegment: async (id: string | number): Promise<Blob> => {
    const url = `${API_BASE_URL}/api/segments/${id}/export`

    // Get token for authorization
    const tokenData = getTokenData()
    const headers: Record<string, string> = {
      'accept': '*/*'
    }
    
    if (tokenData && !isTokenExpired()) {
      headers['Authorization'] = `${tokenData.token_type} ${tokenData.access_token}`
    }

    // Create abort controller for timeout
    const controller = new AbortController()
    const timeoutId = setTimeout(() => controller.abort(), config.apiTimeout)

    try {
      const response = await fetch(url, {
        method: 'GET',
        headers,
        signal: controller.signal
      })

      clearTimeout(timeoutId)

      if (!response.ok) {
        let errorResponse: ApiErrorResponse = {
          timestamp: new Date().toISOString(),
          status: response.status,
          error: 'Export Error',
          message: `Export failed with status ${response.status}`
        }

        try {
          const errorData = await response.json()
          errorResponse = {
            ...errorResponse,
            message: errorData.message || errorResponse.message,
            errors: errorData.errors,
            errorCode: errorData.errorCode
          }
        } catch {
          // Ignore parse errors
        }

        throw new ApiError(errorResponse)
      }

      // Return blob for file download
      return await response.blob()
    } catch (error) {
      clearTimeout(timeoutId)
      if (error instanceof ApiError) {
        throw error
      }
      if (error instanceof Error) {
        if (error.name === 'AbortError') {
          throw new ApiError({
            timestamp: new Date().toISOString(),
            status: 408,
            error: 'Timeout',
            message: 'Request timeout. Please try again.'
          })
        }
        throw error
      }
      throw new Error('An unexpected error occurred')
    }
  }
}

// File upload API functions
export const fileApi = {
  /**
   * Upload a file to the backend using multipart/form-data.
   * Mirrors:
   * curl -X 'POST' 'http://localhost:8080/api/files/upload?folder=richmenus' \
   *   -H 'accept: application/json' \
   *   -H 'Content-Type: multipart/form-data' \
   *   -F 'file=@for-test.jpeg;type=image/jpeg'
   */
  uploadRichMenuImage: async (file: File, folder: string = 'richmenus') => {
    const url = `${API_BASE_URL}/api/files/upload?folder=${encodeURIComponent(folder)}`

    const tokenData = getTokenData()
    const headers: Record<string, string> = {
      accept: 'application/json'
      // Do NOT set Content-Type here; browser will set correct multipart boundary
    }

    if (tokenData && !isTokenExpired()) {
      headers['Authorization'] = `${tokenData.token_type} ${tokenData.access_token}`
    }

    const formData = new FormData()
    formData.append('file', file)

    const controller = new AbortController()
    const timeoutId = setTimeout(() => controller.abort(), config.apiTimeout)

    try {
      const response = await fetch(url, {
        method: 'POST',
        headers,
        body: formData,
        signal: controller.signal
      })

      clearTimeout(timeoutId)

      if (!response.ok) {
        // Try to parse structured error response from API
        let errorResponse: ApiErrorResponse = {
          timestamp: new Date().toISOString(),
          status: response.status,
          error: response.statusText || 'Upload Error',
          message: `Upload failed with status ${response.status}`
        }

        try {
          const contentType = response.headers.get('content-type')
          if (contentType && contentType.includes('application/json')) {
            const errorData = await response.json()
            errorResponse = {
              timestamp: errorData.timestamp || errorResponse.timestamp,
              status: errorData.status || response.status,
              error: errorData.error || errorResponse.error,
              message: errorData.message || errorData.error || errorResponse.message,
              errors: errorData.errors,
              errorCode: errorData.errorCode,
              importType: errorData.importType
            }
          } else {
            const text = await response.text()
            if (text) {
              errorResponse.message = text
            }
          }
        } catch {
          // Ignore parse errors, use default error response
        }

        throw new ApiError(errorResponse)
      }

      const contentType = response.headers.get('content-type')
      if (contentType && contentType.includes('application/json')) {
        return await response.json()
      }

      return (await response.text()) as any
    } catch (error) {
      clearTimeout(timeoutId)
      if (error instanceof ApiError) {
        throw error
      }
      if (error instanceof Error) {
        if (error.name === 'AbortError') {
          throw new ApiError({
            timestamp: new Date().toISOString(),
            status: 408,
            error: 'Timeout',
            message: 'Request timeout. Please try again.'
          })
        }
        throw error
      }
      throw new Error('An unexpected error occurred')
    }
  }
}

// Rich Menu API functions
export const richMenuApi = {
  /**
   * Get rich menu counts for tabs.
   * Returns: { total, totalActive, totalSchedule, totalArchived }
   */
  getCounts: async () => {
    return apiRequest<{
      total: number
      totalActive: number
      totalSchedule: number
      totalArchived: number
    }>(
      '/api/rich-menus/counts',
      {
        method: 'GET',
        requireAuth: true
      }
    )
  },

  /**
   * Get rich menus list with optional filter.
   * @param filter - 'active', 'schedule', 'archived', or undefined for all
   * Returns: { data: RichMenuDataDto[], meta: {...} }
   */
  getRichMenus: async (filter?: 'active' | 'schedule' | 'archived') => {
    return apiRequest<{ data: RichMenuDataDto[], meta: any }>(
      '/api/rich-menus',
      {
        method: 'GET',
        params: filter ? { filter } : undefined,
        requireAuth: true
      }
    )
  },

  /**
   * Get a single rich menu by ID.
   * @param id - Rich menu ID
   */
  getRichMenuById: async (id: string | number) => {
    return apiRequest<RichMenuDataDto>(
      `/api/rich-menus/${id}`,
      {
        method: 'GET',
        requireAuth: true
      }
    )
  },

  /**
   * Create a new rich menu.
   * Expects payload similar to:
   * {
   *   "mode": "create",
   *   "editId": null,
   *   "menuName": "test_1",
   *   "menuBarText": "test_1",
   *   "startDate": "2025-11-28",
   *   "endDate": "2025-11-30",
   *   "showRichMenu": true,
   *   "showOnChatOpen": true,
   *   "targetSegment": "all",
   *   "selectedSegment": null,
   *   "selectedTemplate": { ... },
   *   "actions": [ ... ],
   *   "imageUrl": "https://...png",
   *   "hasImage": true
   * }
   */
  createRichMenu: async (payload: any) => {
    return apiRequest<any>(
      '/api/rich-menus',
      {
        method: 'POST',
        body: payload,
        requireAuth: true
      }
    )
  },

  /**
   * Update an existing rich menu.
   * Expects payload similar to:
   * {
   *   "mode": "edit",
   *   "editId": 123,
   *   "menuName": "Updated Menu",
   *   "menuBarText": "Updated Text",
   *   "startDate": "2025-12-01",
   *   "endDate": "2025-12-31",
   *   "showRichMenu": true,
   *   "targetSegment": "all",
   *   "actions": [ ... ],
   *   "imageUrl": "https://..."
   * }
   */
  updateRichMenu: async (id: string | number, payload: any) => {
    return apiRequest<any>(
      `/api/rich-menus/${id}`,
      {
        method: 'PUT',
        body: payload,
        requireAuth: true
      }
    )
  },

  /**
   * Delete a rich menu by ID.
   * @param id - Rich menu ID
   */
  deleteRichMenu: async (id: string | number) => {
    return apiRequest<void>(
      `/api/rich-menus/${id}`,
      {
        method: 'DELETE',
        requireAuth: true
      }
    )
  },

  /**
   * Toggle active status of a rich menu.
   * @param id - Rich menu ID
   */
  toggleActive: async (id: string | number) => {
    return apiRequest<RichMenuDataDto>(
      `/api/rich-menus/${id}/toggle-active`,
      {
        method: 'PATCH',
        requireAuth: true
      }
    )
  }
}

